import styled, { createGlobalStyle, css, keyframes } from "styled-components";

export const COLORS = {
  bg: "#101118",
  card: "rgba(5, 7, 12, 0.58)",
  cardStrong: "rgba(6, 8, 13, 0.76)",
  line: "rgba(255, 255, 255, 0.07)",
  text: "#ffffff",
  muted: "#b5b6c2",
  blue: "#08a9ff",
  green: "#00edb8",
  yellow: "#f7c736",
  purple: "#8a6cff",
  danger: "#ff4b5f",
};

export const MiniAppGlobalStyle = createGlobalStyle`
  html,
  body,
  #root {
    width: 100%;
    min-width: 0;
    min-height: 100%;
    background: ${COLORS.bg};
    overflow-x: hidden;
    overscroll-behavior-y: none;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  body {
    margin: 0;
    overflow-y: auto;
    overscroll-behavior-y: none;
    touch-action: pan-y;
  }

  html::-webkit-scrollbar,
  body::-webkit-scrollbar,
  #root::-webkit-scrollbar,
  *::-webkit-scrollbar {
    width: 0;
    height: 0;
    display: none;
  }

  * {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
`;

const floatGlow = keyframes`
  0% {
    transform: translate3d(-3%, -2%, 0) scale(1);
    opacity: 0.82;
  }
  50% {
    transform: translate3d(3%, 2%, 0) scale(1.08);
    opacity: 1;
  }
  100% {
    transform: translate3d(-3%, -2%, 0) scale(1);
    opacity: 0.82;
  }
`;

const pageEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

const itemEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

const robotEnter = keyframes`
  0% {
    opacity: 0;
    transform: translate3d(-50%, 44vh, 0) scale(0.98);
  }
  72% {
    opacity: 1;
    transform: translate3d(-50%, -8px, 0) scale(1.01);
  }
  100% {
    opacity: 1;
    transform: translate3d(-50%, 0, 0) scale(1);
  }
`;

const robotMobileEnter = keyframes`
  0% {
    opacity: 0;
    transform: translate3d(-50%, 92px, 0) scale(0.98);
  }
  72% {
    opacity: 1;
    transform: translate3d(-50%, -8px, 0) scale(1.01);
  }
  100% {
    opacity: 1;
    transform: translate3d(-50%, 0, 0) scale(1);
  }
`;

const orderRobotEnter = keyframes`
  0% {
    opacity: 0;
    transform: translate3d(-50%, 92px, 0) scale(0.98);
  }
  72% {
    opacity: 1;
    transform: translate3d(-50%, -7px, 0) scale(1.01);
  }
  100% {
    opacity: 1;
    transform: translate3d(-50%, 0, 0) scale(1);
  }
`;

const leaderRobotEnter = keyframes`
  0% {
    opacity: 0;
    transform: translate3d(0, 86px, 0) scale(0.98);
  }
  72% {
    opacity: 1;
    transform: translate3d(0, -6px, 0) scale(1.01);
  }
  100% {
    opacity: 1;
    transform: translate3d(0, 0, 0) scale(1);
  }
`;

const profileRobotFloat = keyframes`
  0%, 100% {
    transform: translate3d(0, 0, 0);
  }
  50% {
    transform: translate3d(0, -10px, 0);
  }
`;

const buttonGlowPulse = keyframes`
  0%, 100% {
    box-shadow: 0 7px 14px rgba(0, 107, 255, 0.25), 0 0 13px 1px rgba(0, 173, 255, 0.48);
    filter: brightness(1);
  }
  50% {
    box-shadow: 0 8px 18px rgba(0, 107, 255, 0.38), 0 0 25px 4px rgba(0, 190, 255, 0.82);
    filter: brightness(1.13);
  }
`;

const copyNoticeEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const balanceErrorShake = keyframes`
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-5px); }
  40% { transform: translateX(5px); }
  60% { transform: translateX(-4px); }
  80% { transform: translateX(4px); }
`;

const revealDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-14px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const glowBluePosition = (step: number) => {
  const positions = [
    { left: "calc(100vw - 215px)", top: "-78px" },
    { left: "calc(100vw - 280px)", top: "18vh" },
    { left: "calc(100vw - 230px)", top: "calc(100svh - 255px)" },
    { left: "54vw", top: "calc(100svh - 235px)" },
    { left: "34vw", top: "calc(100svh - 288px)" },
    { left: "-76px", top: "-74px" },
    { left: "-96px", top: "24vh" },
    { left: "-86px", top: "calc(100svh - 265px)" },
    { left: "18vw", top: "calc(100svh - 245px)" },
    { left: "calc(100vw - 245px)", top: "42vh" },
    { left: "calc(100vw - 205px)", top: "10vh" },
    { left: "44vw", top: "-86px" },
    { left: "12vw", top: "-68px" },
    { left: "calc(100vw - 235px)", top: "calc(100svh - 265px)" },
  ];
  return positions[Math.abs(step) % positions.length];
};

const glowGreenPosition = (step: number) => {
  const positions = [
    { left: "-104px", top: "calc(100svh - 225px)" },
    { left: "18vw", top: "calc(100svh - 250px)" },
    { left: "-92px", top: "-58px" },
    { left: "28vw", top: "-74px" },
    { left: "calc(100vw - 205px)", top: "-54px" },
    { left: "calc(100vw - 235px)", top: "26vh" },
    { left: "calc(100vw - 220px)", top: "calc(100svh - 235px)" },
    { left: "54vw", top: "calc(100svh - 225px)" },
    { left: "calc(100vw - 205px)", top: "48vh" },
    { left: "-92px", top: "32vh" },
    { left: "8vw", top: "-62px" },
    { left: "calc(100vw - 210px)", top: "8vh" },
    { left: "40vw", top: "calc(100svh - 235px)" },
    { left: "-108px", top: "calc(100svh - 245px)" },
  ];
  return positions[Math.abs(step) % positions.length];
};

export const Shell = styled.div<{ $glowStep?: number }>`
  --app-half: min(50vw, 235px);
  --mini-app-width: min(100%, 470px);
  width: 100%;
  max-width: 100%;
  height: 100vh;
  height: 100dvh;
  max-height: 100dvh;
  min-height: 100vh;
  min-height: 100dvh;
  background: ${COLORS.bg};
  color: ${COLORS.text};
  font-family: "Montserrat", "Inter", sans-serif;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  position: relative;

  &::before,
  &::after {
    content: "";
    position: fixed;
    pointer-events: none;
    border-radius: 999px;
    filter: blur(74px);
    z-index: 0;
    animation: ${floatGlow} 6.5s ease-in-out infinite;
    transition: left 2800ms cubic-bezier(0.16, 1, 0.3, 1), top 2800ms cubic-bezier(0.16, 1, 0.3, 1), opacity 700ms ease;
    will-change: left, top, transform;
  }

  &::before {
    ${({ $glowStep = 0 }) => {
      const position = glowBluePosition($glowStep);
      return css`
        left: ${position.left};
        top: ${position.top};
      `;
    }}
    width: 270px;
    height: 270px;
    background: rgba(8, 169, 255, 0.45);
  }

  &::after {
    ${({ $glowStep = 0 }) => {
      const position = glowGreenPosition($glowStep);
      return css`
        left: ${position.left};
        top: ${position.top};
      `;
    }}
    width: 230px;
    height: 230px;
    background: rgba(0, 237, 184, 0.16);
    animation-delay: -2.5s;
  }

  html[data-platform="ios"] &::before,
  html[data-platform="ios"] &::after {
    position: absolute;
    filter: none;
    border-radius: 0;
    animation: none;
    will-change: auto;
  }

  html[data-platform="ios"] &::before {
    background: radial-gradient(circle, rgba(8, 169, 255, 0.45) 0%, rgba(8, 169, 255, 0.2) 42%, rgba(8, 169, 255, 0) 72%);
  }

  html[data-platform="ios"] &::after {
    background: radial-gradient(circle, rgba(0, 237, 184, 0.18) 0%, rgba(0, 237, 184, 0.08) 44%, rgba(0, 237, 184, 0) 72%);
  }

  @media (max-width: 640px) {
    --app-half: 50vw;

    &::before {
      ${({ $glowStep = 0 }) => {
        const position = glowBluePosition($glowStep);
        const mobilePositions = [
          { left: "calc(100vw - 150px)", top: "-70px" },
          { left: "calc(100vw - 165px)", top: "calc(100svh - 220px)" },
          { left: "-92px", top: "calc(100svh - 225px)" },
          { left: "-82px", top: "-68px" },
        ];
        const mobile = mobilePositions[Math.abs($glowStep) % mobilePositions.length] || position;
        return css`
          left: ${mobile.left};
          top: ${mobile.top};
        `;
      }}
      width: 230px;
      height: 230px;
      filter: blur(62px);
    }

    &::after {
      ${({ $glowStep = 0 }) => {
        const position = glowGreenPosition($glowStep);
        const mobilePositions = [
          { left: "-100px", top: "calc(100svh - 205px)" },
          { left: "-88px", top: "-58px" },
          { left: "calc(100vw - 155px)", top: "-56px" },
          { left: "calc(100vw - 170px)", top: "calc(100svh - 215px)" },
        ];
        const mobile = mobilePositions[Math.abs($glowStep) % mobilePositions.length] || position;
        return css`
          left: ${mobile.left};
          top: ${mobile.top};
        `;
      }}
      width: 205px;
      height: 205px;
      filter: blur(58px);
    }
  }
`;

export const PhoneTop = styled.div`
  position: sticky;
  top: 0;
  z-index: 20;
`;

export const StatusBar = styled.div`
  height: 50px;
  background: #fff;
  color: #000;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding: 0 24px 10px;
  box-sizing: border-box;
  font-family: "Roboto", sans-serif;
  font-size: 14px;
  font-weight: 500;

  &::after {
    content: "";
    position: absolute;
    left: 50%;
    top: 18px;
    transform: translateX(-50%);
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #1d1b20;
  }
`;

export const TopIcons = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;

  span {
    display: block;
    background: #111;
  }
`;

export const TelegramBar = styled.div`
  height: 38px;
  background: #fff;
  color: #000;
  display: grid;
  grid-template-columns: 34px 1fr 34px;
  align-items: center;
  padding: 0 14px;
  box-sizing: border-box;
  font-family: "Montserrat", sans-serif;
  font-size: 17px;
  font-weight: 700;
`;

export const Page = styled.main<{ $short?: boolean; $home?: boolean }>`
  --mini-page-x: 24px;
  width: min(100%, 470px);
  max-width: 470px;
  flex: none;
  min-height: ${({ $short }) => ($short ? "calc(100vh - 88px)" : "100vh")};
  margin: 0;
  padding: max(64px, calc(env(safe-area-inset-top, 0px) + 42px)) var(--mini-page-x)
    calc(156px + env(safe-area-inset-bottom, 0px));
  box-sizing: border-box;
  position: relative;
  z-index: 0;
  overflow: visible;
  overscroll-behavior-y: contain;
  overflow-anchor: none;
  touch-action: pan-y;
  animation: ${pageEnter} 460ms cubic-bezier(0.2, 0.8, 0.2, 1) both;

  > * {
    animation: ${itemEnter} 380ms ease both;
  }

  > *:nth-child(2) {
    animation-delay: 45ms;
  }

  > *:nth-child(3) {
    animation-delay: 90ms;
  }

  > *:nth-child(4) {
    animation-delay: 135ms;
  }

  > *:nth-child(n + 5) {
    animation-delay: 170ms;
  }

  @media (max-width: 640px) {
    --mini-page-x: 14px;
    width: 100%;
    max-width: none;
    flex-basis: 100%;
    padding-top: max(116px, calc(env(safe-area-inset-top, 0px) + 86px));
    ${({ $home }) =>
      $home &&
      css`
        min-height: 100svh;
        overflow: visible;
        padding-bottom: calc(76px + env(safe-area-inset-bottom, 0px));
      `}
  }
`;

export const ScreenStage = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  justify-items: center;
  align-items: start;
  overflow-x: hidden;
  animation: ${pageEnter} 520ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
`;

export const DataLoadingOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 90;
  background: ${COLORS.bg};
  pointer-events: none;
  animation: ${pageEnter} 260ms ease both;

  > div {
    min-height: 100vh;
    height: 100svh;
  }
`;

export const H1 = styled.h1`
  margin: 0;
  font-family: "Unbounded", "Montserrat", sans-serif;
  font-size: 24px;
  line-height: 1.1;
  font-weight: 700;
  letter-spacing: 0;
`;

export const Lead = styled.p`
  margin: 12px 0 0;
  color: ${COLORS.muted};
  font-size: 12.5px;
  line-height: 1.35;
  font-weight: 500;

  @media (max-width: 430px) {
    margin-top: 10px;
    font-size: 12px;
    line-height: 1.28;
  }
`;

export const SectionTitle = styled.h2`
  margin: 22px 0 14px;
  font-size: 16px;
  line-height: 1.25;
  font-weight: 700;
  cursor: pointer;

  @media (max-width: 430px) {
    margin: 18px 0 12px;
  }
`;

export const Card = styled.div<{ $active?: boolean; $compact?: boolean }>`
  position: relative;
  background: ${({ $active }) => ($active ? COLORS.blue : COLORS.card)};
  border: 1px solid ${COLORS.line};
  border-radius: ${({ $compact }) => ($compact ? "14px" : "18px")};
  box-sizing: border-box;
  overflow: hidden;
  backdrop-filter: blur(9px);
  transition: background-color 260ms ease, border-color 260ms ease, box-shadow 260ms ease, transform 180ms ease;

  html[data-platform="ios"] & {
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }
`;

export const BalanceCard = styled(Card)`
  min-height: 120px;
  margin-top: 18px;
  padding: 16px 134px 16px 13px;

  @media (max-width: 430px) {
    min-height: 112px;
    margin-top: 16px;
    padding: 13px 132px 13px 12px;
  }
`;

export const CTAHint = styled.div`
  position: absolute;
  top: 18px;
  right: 16px;
  width: 168px;
  text-align: right;
  color: rgba(0, 237, 184, 0.9);
  font-family: "Inter", sans-serif;
  font-size: 10px;
  line-height: 1.18;

  b {
    display: block;
    margin-bottom: 4px;
    font-size: 12px;
  }

  @media (max-width: 430px) {
    top: 17px;
    right: 13px;
    width: 178px;
    font-size: 10.2px;
    line-height: 1.18;

    b {
      font-size: 12.2px;
    }
  }
`;

export const GlowButton = styled.button`
  border: 0;
  height: 34px;
  border-radius: 12px;
  background: ${COLORS.blue};
  color: #fff;
  font-family: "Inter", sans-serif;
  font-size: 10.2px;
  font-weight: 700;
  box-shadow:
    0 7px 14px rgba(0, 107, 255, 0.25),
    0 0 15px 1px rgba(0, 173, 255, 0.62);
  cursor: pointer;
  animation: ${buttonGlowPulse} 2.1s ease-in-out infinite;
  transition: transform 180ms ease;

  &:active {
    transform: scale(0.98);
  }
`;

export const PrimaryButton = styled.button`
  width: 100%;
  height: 60px;
  border: 0;
  border-radius: 14px;
  background: ${COLORS.blue};
  color: #fff;
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 260ms ease, color 260ms ease, opacity 260ms ease, transform 180ms ease;

  &:disabled {
    background: #555861;
    color: rgba(255, 255, 255, 0.62);
    cursor: default;
    box-shadow: none;
    opacity: 0.78;
  }

  &:not(:disabled):active {
    transform: scale(0.99);
  }
`;

export const GhostButton = styled.button`
  width: 100%;
  height: 60px;
  min-height: 60px;
  border: 1px solid ${COLORS.line};
  border-radius: 14px;
  background: rgba(5, 7, 12, 0.6);
  color: #fff;
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 260ms ease, border-color 260ms ease, color 260ms ease, transform 180ms ease;

  &:active {
    transform: scale(0.98);
  }
`;

export const CopyNotice = styled.div<{ $success?: boolean }>`
  position: relative;
  z-index: 3;
  min-height: 34px;
  border-radius: 10px;
  background: ${({ $success = true }) => ($success ? "rgba(0, 237, 184, 0.16)" : "rgba(255, 75, 95, 0.16)")};
  border: 1px solid ${({ $success = true }) => ($success ? "rgba(0, 237, 184, 0.34)" : "rgba(255, 75, 95, 0.34)")};
  color: ${({ $success = true }) => ($success ? COLORS.green : COLORS.danger)};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 800;
  animation: ${copyNoticeEnter} 260ms cubic-bezier(0.16, 1, 0.3, 1) both;
`;

export const ProductsGrid = styled.div`
  position: relative;
  z-index: 12;
  isolation: isolate;
  transform: translateZ(0);
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 430px) {
    gap: 14px;
  }
`;

export const ProductTile = styled(Card)`
  z-index: 1;
  background: #05070c;
  min-height: 126px;
  padding: 13px;
  cursor: pointer;

  @media (max-width: 430px) {
    min-height: 116px;
    padding: 9px 10px;

    img {
      width: 38px;
      height: 38px;
    }
  }
`;

export const Icon = styled.img<{ $size?: number }>`
  width: ${({ $size }) => $size || 52}px;
  height: ${({ $size }) => $size || 52}px;
  object-fit: contain;
  flex: 0 0 auto;
`;

export const Price = styled.div<{ $tone?: "yellow" | "blue" | "green" | "purple" }>`
  margin-top: 14px;
  font-family: "Unbounded", "Montserrat", sans-serif;
  font-size: 16px;
  line-height: 1.25;
  font-weight: 800;
  color: ${({ $tone }) =>
    $tone === "blue" ? COLORS.blue : $tone === "green" ? "#23d18b" : $tone === "purple" ? COLORS.purple : COLORS.yellow};

  @media (max-width: 430px) {
    margin-top: 8px;
    font-size: 13.5px;
  }
`;

export const RowCard = styled(Card)<{ $selected?: boolean }>`
  min-height: 92px;
  padding: 15px 16px;
  display: grid;
  grid-template-columns: 46px 1fr auto;
  gap: 8px;
  align-items: center;
  cursor: pointer;
  transition: background-color 280ms ease, border-color 280ms ease, box-shadow 280ms ease, transform 180ms ease;
  ${({ $selected }) =>
    $selected &&
    css`
      background: ${COLORS.blue};
      color: #fff;

      p {
        color: #fff;
      }
    `}
`;

export const Chips = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 18px;
`;

export const Chip = styled.button<{ $active?: boolean }>`
  height: 34px;
  border: 1px solid ${COLORS.line};
  border-radius: 17px;
  padding: 0 18px;
  background: ${({ $active }) => ($active ? COLORS.blue : "rgba(5, 7, 12, 0.6)")};
  color: #fff;
  font-family: "Montserrat", sans-serif;
  font-size: 11.5px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 260ms ease, border-color 260ms ease, color 260ms ease, box-shadow 260ms ease, transform 180ms ease;

  &:active {
    transform: scale(0.96);
  }
`;

export const DurationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 13px 10px;
`;

export const DurationButton = styled.button<{ $active?: boolean; $wide?: boolean }>`
  grid-column: ${({ $wide }) => ($wide ? "1 / -1" : "auto")};
  height: 56px;
  border: 1px solid ${COLORS.line};
  border-radius: 16px;
  background: ${({ $active }) => ($active ? COLORS.blue : COLORS.card)};
  color: #fff;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 13px 16px;
  box-sizing: border-box;
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 280ms ease, border-color 280ms ease, box-shadow 280ms ease, transform 180ms ease;

  &:active {
    transform: scale(0.98);
  }
`;

export const Summary = styled(Card)`
  padding: 17px;
`;

export const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 18px;
`;

export const MetricCard = styled(Card)<{ $accent?: "blue" | "green" | "purple" | "yellow" }>`
  min-height: 92px;
  padding: 14px;

  strong {
    display: block;
    margin-top: 8px;
    font-family: "Unbounded", "Montserrat", sans-serif;
    font-size: 19px;
    line-height: 1.15;
    font-weight: 900;
    color: ${({ $accent }) =>
      $accent === "green" ? COLORS.green : $accent === "purple" ? COLORS.purple : $accent === "yellow" ? COLORS.yellow : COLORS.blue};
  }
`;

export const LeaderHero = styled(Card)`
  min-height: 118px;
  margin-top: 20px;
  padding: 16px;
  display: grid;
  grid-template-columns: 70px 1fr;
  gap: 14px;
  align-items: center;
  background: linear-gradient(121deg, rgba(5, 7, 12, 0.84), rgba(9, 85, 145, 0.34));
`;

export const DesignPage = styled(Page)<{ $flush?: boolean; $leaders?: boolean }>`
  width: min(100%, 470px);
  max-width: 470px;
  flex: none;
  padding: ${({ $leaders }) => ($leaders ? 0 : 16)}px ${({ $flush }) => ($flush ? 0 : 14)}px calc(76px + env(safe-area-inset-bottom, 0px));
  background: transparent;
  min-height: 100vh;
  overflow: visible;
  touch-action: pan-y;

  @media (max-width: 640px) {
    width: 100%;
    max-width: none;
    flex-basis: 100%;
    padding-top: ${({ $leaders }) => ($leaders ? "0" : "max(132px, calc(env(safe-area-inset-top, 0px) + 104px))")};
    padding-bottom: calc(140px + env(safe-area-inset-bottom, 0px));
  }
`;

export const LeaderBoard = styled.div`
  margin: 0;
  overflow: hidden;
`;

export const LeaderTop = styled.div`
  position: relative;
  box-sizing: border-box;
  height: clamp(178px, 53.5vw, 252px);
  padding: clamp(70px, 21vw, 99px) clamp(128px, 36vw, 169px) 0 clamp(24px, 7.2vw, 34px);
  display: grid;
  grid-template-columns: clamp(88px, 26.4vw, 124px) 1fr;
  gap: clamp(13px, 3.9vw, 18px);
  align-items: start;
  background: linear-gradient(90deg, rgba(23, 24, 32, 0.28) 0%, rgba(17, 32, 48, 0.36) 48%, rgba(14, 18, 27, 0.18) 100%);
  overflow: hidden;

  .leader-title {
    position: absolute;
    left: clamp(24px, 7.2vw, 34px);
    top: clamp(31px, 9.3vw, 44px);
    z-index: 2;
    margin: 0;
    font-size: clamp(18px, 5.4vw, 25px);
    line-height: 1.16;
  }

  > img.robot {
    position: absolute;
    right: clamp(-86px, -18vw, -64px);
    top: clamp(24px, 5.6vw, 32px);
    width: clamp(288px, 68vw, 320px);
    height: clamp(288px, 68vw, 320px);
    object-fit: contain;
    object-position: top left;
    animation: ${leaderRobotEnter} 1080ms cubic-bezier(0.14, 0.86, 0.22, 1.08) 420ms both;
    will-change: opacity, transform;
  }

  @media (max-width: 640px) {
    height: clamp(292px, 73vw, 332px);
    padding-top: clamp(150px, 38vw, 178px);

    .leader-title {
      top: clamp(124px, 31vw, 148px);
    }

    > img.robot {
      top: clamp(86px, 22vw, 106px);
    }
  }
`;

export const HexAvatar = styled.div<{ $src?: string; $large?: boolean }>`
  width: ${({ $large }) => ($large ? "clamp(88px, 26.4vw, 124px)" : "clamp(54px, 16.2vw, 76px)")};
  height: ${({ $large }) => ($large ? "clamp(88px, 26.4vw, 124px)" : "clamp(54px, 16.2vw, 76px)")};
  clip-path: polygon(24% 4%, 76% 4%, 100% 30%, 100% 70%, 76% 96%, 24% 96%, 0 70%, 0 30%);
  background:
    ${({ $src }) => ($src ? `url(${$src}) center/cover no-repeat,` : "")}
    linear-gradient(135deg, rgba(8, 169, 255, 0.45), rgba(138, 108, 255, 0.54));
  box-shadow: inset 0 0 0 3px rgba(8, 169, 255, 0.72);
`;

export const LeaderTableRow = styled.div`
  position: relative;
  box-sizing: border-box;
  height: clamp(73px, 21.92vw, 103px);
  padding: clamp(8px, 2.4vw, 11px) clamp(22px, 6.6vw, 31px) clamp(8px, 2.4vw, 11px) clamp(34px, 10.2vw, 48px);
  display: grid;
  grid-template-columns: clamp(54px, 16.2vw, 76px) minmax(0, 1fr);
  gap: clamp(10px, 3vw, 14px);
  align-items: center;
  background: rgba(32, 52, 70, 0.78);
  border-bottom: 3px solid rgba(17, 17, 23, 0.92);
  backdrop-filter: blur(8px);

  html[data-platform="ios"] & {
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }

  &:nth-child(even) {
    background: rgba(29, 48, 65, 0.7);
  }
`;

export const DesignStatCard = styled.div`
  min-height: 78px;
  border-radius: 14px;
  padding: 12px 16px;
  background: linear-gradient(100deg, rgba(64, 62, 68, 0.95), rgba(43, 84, 116, 0.86));
  box-sizing: border-box;
  overflow: hidden;
  border: 0;
  outline: 0;
`;

export const Donut = styled.div<{ $percentage?: number }>`
  width: 210px;
  height: 210px;
  margin: 8px auto 16px;
  border-radius: 50%;
  background: ${({ $percentage = 0 }) => `conic-gradient(#11a8f4 0 ${Math.max(0, Math.min(100, $percentage)) * 3.6}deg, #e51d24 ${Math.max(0, Math.min(100, $percentage)) * 3.6}deg 360deg)`};
  display: grid;
  place-items: center;
  position: relative;

  &::before {
    content: "";
    width: 134px;
    height: 134px;
    border-radius: 50%;
    background: #3c3a3f;
    position: absolute;
  }

  > div {
    position: relative;
    z-index: 1;
    text-align: center;
  }
`;

export const Avatar = styled.img<{ $size?: number }>`
  width: ${({ $size }) => $size || 54}px;
  height: ${({ $size }) => $size || 54}px;
  border-radius: 16px;
  object-fit: cover;
  background: linear-gradient(135deg, rgba(8, 169, 255, 0.28), rgba(138, 108, 255, 0.42));
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

export const LeaderList = styled.div`
  display: grid;
  gap: 12px;
  margin-top: 16px;
`;

export const LeaderRow = styled(Card)`
  min-height: 76px;
  padding: 11px 12px;
  display: grid;
  grid-template-columns: 50px 1fr auto;
  gap: 11px;
  align-items: center;
`;

export const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 8px;
`;

export const Pill = styled.span<{ $tone?: "blue" | "green" | "purple" | "dark" }>`
  min-width: 36px;
  height: 22px;
  padding: 0 9px;
  border-radius: 11px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  background: ${({ $tone }) =>
    $tone === "green" ? "rgba(0, 237, 184, 0.18)" : $tone === "purple" ? "rgba(138, 108, 255, 0.2)" : $tone === "dark" ? "rgba(5, 7, 12, 0.62)" : COLORS.blue};
  color: ${({ $tone }) => ($tone === "green" ? COLORS.green : "#fff")};
  font-size: 10px;
  line-height: 1;
  font-weight: 800;
`;

export const ReferralCard = styled(Card)`
  margin-top: 20px;
  padding: 18px;
  min-height: 210px;
  overflow: visible;
  background:
    radial-gradient(circle at 88% 16%, rgba(8, 169, 255, 0.28), transparent 35%),
    rgba(5, 7, 12, 0.58);
`;

export const ProfileReferralRobot = styled.img`
  position: absolute;
  right: -58px;
  top: -20px;
  width: 260px;
  height: 260px;
  object-fit: contain;
  object-position: center top;
  z-index: 1;
  pointer-events: none;
  animation:
    ${leaderRobotEnter} 1080ms cubic-bezier(0.14, 0.86, 0.22, 1.08) 260ms both,
    ${profileRobotFloat} 3.2s ease-in-out 1340ms infinite;
  will-change: opacity, transform;

  @media (max-width: 430px) {
    right: -132px;
    top: -18px;
    width: 252px;
    height: 252px;
  }
`;

export const StatsPanel = styled(Card)`
  margin-top: 18px;
  padding: 16px;
  border: 0;
  outline: 0;
  box-shadow: none;
`;

export const ProgressTrack = styled.div`
  width: 100%;
  height: 9px;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.08);
`;

export const ProgressFill = styled.div<{ $value: number; $tone?: "blue" | "green" | "purple" }>`
  width: ${({ $value }) => Math.max(0, Math.min(100, $value))}%;
  height: 100%;
  border-radius: inherit;
  background: ${({ $tone }) => ($tone === "green" ? COLORS.green : $tone === "purple" ? COLORS.purple : COLORS.blue)};
  box-shadow: 0 0 16px ${({ $tone }) => ($tone === "green" ? "rgba(0,237,184,.44)" : $tone === "purple" ? "rgba(138,108,255,.44)" : "rgba(8,169,255,.44)")};
`;

export const SummaryRow = styled.div<{ $total?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  color: ${({ $total }) => ($total ? "#fff" : COLORS.muted)};
  font-size: ${({ $total }) => ($total ? "13px" : "12px")};
  font-weight: ${({ $total }) => ($total ? 700 : 500)};
  line-height: 1.25;
  padding: 8px 0;

  strong {
    color: #fff;
    font-family: ${({ $total }) => ($total ? '"Unbounded", "Montserrat", sans-serif' : "inherit")};
    font-size: ${({ $total }) => ($total ? "17px" : "12px")};
  }
`;

export const Input = styled.input<{ $error?: boolean }>`
  width: 100%;
  height: 52px;
  border: 1px solid ${({ $error }) => ($error ? COLORS.danger : COLORS.line)};
  border-radius: 14px;
  background: rgba(5, 7, 12, 0.72);
  color: #fff;
  box-sizing: border-box;
  padding: 0 54px 0 15px;
  font-family: "Montserrat", sans-serif;
  font-size: 13px;
  outline: none;
`;

export const SupportChat = styled(Card)`
  height: calc(100dvh - 240px);
  min-height: 440px;
  margin-top: 18px;
  padding: 14px 12px 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${itemEnter} 480ms cubic-bezier(0.22, 1, 0.36, 1) both;

  @media (max-width: 640px) {
    height: calc(100dvh - 300px);
    min-height: 390px;
  }
`;

export const SupportMessages = styled.div`
  flex: 1;
  min-height: 250px;
  overflow-y: auto;
  padding: 0 0 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const SupportBubble = styled.div<{ $operator?: boolean }>`
  width: fit-content;
  max-width: min(86%, 420px);
  align-self: ${({ $operator }) => ($operator ? "flex-end" : "flex-start")};
  display: grid;
  grid-template-columns: 42px minmax(0, auto);
  gap: 9px;
  padding: 12px;
  border-radius: 16px;
  border: 1px solid ${COLORS.line};
  background: ${({ $operator }) => ($operator ? "rgba(7, 11, 19, 0.88)" : "rgba(14, 39, 62, 0.82)")};
  box-sizing: border-box;
  animation: ${itemEnter} 360ms cubic-bezier(0.22, 1, 0.36, 1) both;
`;

export const SupportComposer = styled.form`
  display: grid;
  grid-template-columns: 46px minmax(0, 1fr) 46px;
  gap: 8px;
  align-items: end;
  padding-top: 10px;
  border-top: 1px solid ${COLORS.line};
`;

export const SupportTextarea = styled.textarea`
  width: 100%;
  height: 46px;
  min-height: 46px;
  max-height: 108px;
  resize: none;
  border: 1px solid ${COLORS.line};
  border-radius: 12px;
  background: rgba(5, 7, 12, 0.76);
  color: #fff;
  box-sizing: border-box;
  padding: 0 13px;
  overflow: hidden;
  font: 500 12px/44px "Montserrat", sans-serif;
  outline: none;

  &:focus {
    border-color: rgba(8, 169, 255, 0.7);
  }
`;

export const SupportSendButton = styled.button`
  width: 46px;
  height: 46px;
  border: 0;
  border-radius: 12px;
  background: ${COLORS.blue};
  color: #fff;
  font-size: 23px;
  font-weight: 800;
  cursor: pointer;
  display: grid;
  place-items: center;
  padding: 0;
  transition: opacity 180ms ease, transform 180ms ease;

  svg {
    display: block;
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }

  &:active:not(:disabled) {
    transform: scale(0.96);
  }
`;

export const SupportAttachButton = styled(SupportSendButton)`
  background: rgba(7, 11, 19, 0.78);
  border: 1px solid ${COLORS.line};
  color: #aeb9c8;
`;

export const SupportAttachment = styled.div`
  grid-column: 1 / -1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid ${COLORS.line};
  border-radius: 10px;
  background: rgba(7, 11, 19, 0.72);
  color: #dce6f3;
  font-size: 11px;

  span {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  button {
    border: 0;
    padding: 2px;
    background: transparent;
    color: #aeb9c8;
    cursor: pointer;
  }
`;

export const SupportMedia = styled.div`
  margin-top: 8px;

  img, video {
    display: block;
    width: 100%;
    max-height: 220px;
    object-fit: contain;
    border-radius: 9px;
  }

  audio { width: 100%; }

  a {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #32b7ff;
    font-size: 11px;
    text-decoration: none;
  }
`;

export const ErrorLine = styled.div`
  margin-top: 7px;
  color: ${COLORS.danger};
  font-size: 10.5px;
  line-height: 1.25;
  font-weight: 700;
`;

export const BottomNav = styled.nav`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  width: auto;
  height: calc(76px + env(safe-area-inset-bottom, 0px));
  background: #121219;
  display: grid;
  grid-template-columns: repeat(4, 108px);
  justify-content: center;
  align-items: start;
  padding-top: 8px;
  padding-bottom: env(safe-area-inset-bottom, 0px);
  box-sizing: border-box;
  z-index: 30;

  @media (max-width: 640px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    justify-content: stretch;
  }
`;

export const NavItem = styled.button<{ $active?: boolean }>`
  border: 0;
  background: transparent;
  color: ${({ $active }) => ($active ? COLORS.blue : "rgba(255, 255, 255, 0.94)")};
  font-family: "Inter", sans-serif;
  font-size: 10px;
  font-weight: 500;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 3px;
  min-width: 0;
  cursor: pointer;
`;

export const NavIcon = styled.img<{ $active?: boolean }>`
  width: 35px;
  height: 35px;
  object-fit: contain;
`;

export const RobotDecor = styled.img`
  display: block;
  position: absolute;
  left: 50%;
  bottom: calc(76px + env(safe-area-inset-bottom, 0px));
  z-index: 1;
  width: 310px;
  height: 184px;
  object-fit: cover;
  object-position: center top;
  margin: 0;
  pointer-events: none;
  animation: ${robotEnter} 1080ms cubic-bezier(0.14, 0.86, 0.22, 1.08) 1180ms both;
  animation-delay: 1180ms !important;
  will-change: opacity, transform;

  @media (max-width: 640px) {
    position: relative;
    left: 50%;
    bottom: auto;
    z-index: 2;
    width: 310px;
    height: 184px;
    margin: 18px 0 0;
    object-position: center top;
    opacity: 0;
    transform: translate3d(-50%, 92px, 0) scale(0.98);
    animation: none;

    &.robot-visible {
      animation: ${robotMobileEnter} 1080ms cubic-bezier(0.14, 0.86, 0.22, 1.08) both;
    }
  }
`;

export const OrderRobot = styled.img`
  animation: ${orderRobotEnter} 1080ms cubic-bezier(0.14, 0.86, 0.22, 1.08) 620ms both;
  will-change: opacity, transform;
`;

export const SmallText = styled.p`
  margin: 5px 0 0;
  color: ${COLORS.muted};
  font-size: 10.5px;
  line-height: 1.25;

  @media (max-width: 430px) {
    font-size: 9.4px;
  }
`;

export const BalanceErrorText = styled(SmallText)<{ $shakeKey?: number }>`
  color: ${COLORS.danger};
  animation: ${balanceErrorShake} 360ms ease both;
`;

export const AnimatedReveal = styled.div`
  animation: ${revealDown} 420ms cubic-bezier(0.16, 1, 0.3, 1) both;
`;

export const TitleText = styled.div`
  font-size: 15px;
  line-height: 1.25;
  font-weight: 700;

  @media (max-width: 430px) {
    font-size: 13.5px;
  }
`;
